import React, { useState, useEffect } from 'react';
import { CognitoUserAmplify } from '@aws-amplify/ui';
import { AuthEventData } from '@aws-amplify/ui';
import { Auth } from '@aws-amplify/auth';
import Header from 'components/Header/Header';
import NavPanel from 'components/NavPanel/NavPanel';
import Footer from 'components/Footer/Footer';
import Home from 'pages/Home/Home';
import About from 'pages/About/About';
import Picks from 'pages/Picks/Picks';
import PickDetail from 'pages/PickDetail/PickDetail';
import OurBet from 'pages/OurBet/OurBet';
import PickInsights from 'pages/PickInsights/PickInsights';
import TodaysPick from 'pages/TodaysPick/TodaysPick';
import Contact from 'pages/Contact/Contact';
import MyAccount from 'pages/MyAccount/MyAccount';
import Pricing from 'pages/Pricing/Pricing';
import { GROUP_NAMES } from 'utils';
import { generateNavOptions, generateFooterChildren } from '../../helpers/componentHelper';

import Router from '../../routes/Router';

import './AppContainer.scss';

type AppContainerComponentProps = {
  user: CognitoUserAmplify;
  signOut: (data?: AuthEventData) => void;
};

function AppContainer({ user, signOut }: AppContainerComponentProps) {
  const [showNav, setShowNav] = useState(false);
  const [userGroup, setUserGroup] = useState<string>('');
  const [userGroups, setUserGroups] = useState<string[]>([]);

  useEffect(() => {
    const getUserGroups = async () => {
      // Retrieve the current active session
      const session = await Auth.currentSession();
      const groups = session.getAccessToken()?.payload['cognito:groups'] as string[];
      if (groups) {
        setUserGroup(groups[0]?.substring(0, groups[0]?.length - 4).toUpperCase());
        setUserGroups([...groups]);
      } else {
        const freeGroup = GROUP_NAMES.free.substring(0, GROUP_NAMES.free.length - 4).toUpperCase();
        setUserGroup(freeGroup);
        setUserGroups([GROUP_NAMES.free]);
      }
    };
    getUserGroups();
  }, []);

  const childrenMap = {
    home: (
      <div className="app__container--page-body">
        <Home userGroup={userGroup} />
      </div>
    ),
    pricing: (
      <div className="app__container--page-body">
        <Pricing userGroup={userGroup} />
      </div>
    ),
    about: (
      <div className="app__container--page-body">
        <About />
      </div>
    ),
    picks: (
      <div className="app__container--page-body">
        <Picks userGroups={userGroups} />
      </div>
    ),
    pickDetail: (
      <div className="app__container--page-body">
        <PickDetail />
      </div>
    ),
    ourBet: (
      <div className="app__container--page-body">
        <OurBet userGroups={userGroups} />
      </div>
    ),
    pickInsights: (
      <div className="app__container--page-body">
        <PickInsights userGroups={userGroups} />
      </div>
    ),
    todaysPick: (
      <div className="app__container--page-body">
        <TodaysPick userGroups={userGroups} />
      </div>
    ),
    contact: (
      <div className="app__container--page-body">
        <Contact />
      </div>
    ),
    account: (
      <div className="app__container--page-body">
        <MyAccount userGroup={userGroup} loggedInUser={user} />
      </div>
    ),
  };

  const navOptions = generateNavOptions(() => setShowNav(false), userGroups);
  const footerChildren = generateFooterChildren();

  return (
    <div className="app__container">
      <Header onShowNav={() => setShowNav(!showNav)} showNav={showNav} />
      <NavPanel
        showNav={showNav}
        options={navOptions}
        loggedInUser={user}
        userTier={userGroup}
        signOut={signOut}
      />
      <Router childrenMap={childrenMap} />
      <Footer children={footerChildren} />
    </div>
  );
}

export default AppContainer;
